import type { Offset } from 'maplibre-gl';

/**
 * Shared map-marker pixel dimensions. POI and air-valve markers render at the same size, so the
 * canonical values live here and both layers (useEntitiesLayer, airValveMarkers) derive from them —
 * resize once and the two layers stay in lock-step instead of silently drifting apart.
 */
export const MAP_MARKER_WIDTH = 52;
export const MAP_MARKER_HEIGHT = 60;

/**
 * Per-anchor `offset` for a MapLibre `<Popup>` anchored to a bottom-pinned marker (POI,
 * air-valve): the pin tip sits on the geo coordinate and the marker body extends upward. When
 * the popup is placed above the point it must clear the visible pin; when placed below, the
 * marker is out of the way so only a small gap is needed. MapLibre keys the offset by the
 * popup's chosen anchor, so we give the upward (`bottom*`) anchors the full clearance and the
 * others a small gap. A scalar offset cannot express this asymmetry.
 *
 * Clearance is measured against the *visible* pin, not the canvas. The marker canvas is
 * MAP_MARKER_HEIGHT (60px) but it is bottom-anchored with a +6px icon-offset and the pin
 * graphic stops ~4px below the canvas top, so the pin only rises ~MARKER_VISIBLE_RISE (50px)
 * above the coordinate. The previous clearance cleared the whole 60px canvas, leaving a ~10px
 * overshoot on top of the gap — i.e. the card-above gap was really ~18px, which is why nudging
 * MARKER_POPUP_GAP alone barely moved it. Both gaps are now ~halved against the visible pin.
 */
const MARKER_VISIBLE_RISE = 50;
const MARKER_POPUP_BELOW_GAP = 2; // card under the marker (top* anchors): was 8
const MARKER_POPUP_ABOVE_GAP = 4; // card over the marker (bottom* anchors): was ~18
const MARKER_POPUP_CLEARANCE = MARKER_VISIBLE_RISE + MARKER_POPUP_ABOVE_GAP;
export const MARKER_ANCHORED_POPUP_OFFSET: Offset = {
    top: [0, MARKER_POPUP_BELOW_GAP],
    'top-left': [0, MARKER_POPUP_BELOW_GAP],
    'top-right': [0, MARKER_POPUP_BELOW_GAP],
    bottom: [0, -MARKER_POPUP_CLEARANCE],
    'bottom-left': [0, -MARKER_POPUP_CLEARANCE],
    'bottom-right': [0, -MARKER_POPUP_CLEARANCE],
    left: [MARKER_POPUP_BELOW_GAP, -MARKER_POPUP_CLEARANCE / 2],
    right: [-MARKER_POPUP_BELOW_GAP, -MARKER_POPUP_CLEARANCE / 2],
    center: [0, 0],
};
